import type { Dispatch, SecodaEditorComponentProps } from '@repo/secoda-editor';
import type { MarkdownSerializerState } from '@repo/secoda-editor/lib/markdown/serializer';
import type Token from 'markdown-it/lib/token';
import type { Node, NodeSpec } from 'prosemirror-model';
import type { EditorState } from 'prosemirror-state';
import type { ReactElement } from 'react';

import type { NodeOptions } from '@repo/secoda-editor/nodes/Node';
import ReactNode from '@repo/secoda-editor/nodes/ReactNode';
import { resourceLinksRule } from '@repo/secoda-editor/rules/resourceLink';
import { ResourceLink as ReactResourceLink } from '../../../../Link';

export default class ResourceLink extends ReactNode {
	get name() {
		return 'resource_link';
	}

	get schema(): NodeSpec {
		return {
			content: 'text*',
			inline: true,
			group: 'inline',
			atom: true,
			attrs: {
				href: {
					default: '',
				},
			},
			parseDOM: [
				{
					tag: 'a[href]',
					getAttrs: (dom: string | HTMLElement) => ({
						href: (dom as HTMLElement).getAttribute('href') ?? '',
					}),
				},
			],
			toDOM: (node) => [
				'a',
				{
					href: node.attrs.href,
					contentEditable: 'false',
				},
				0,
			],
		};
	}

	get rulePlugins() {
		return [resourceLinksRule];
	}

	inputRules() {
		// DO NOT DEFINE AN INPUT RULE FOR THIS NODE. THE LINK MARK WILL HANDLE THIS.
		return [];
	}

	component = (props: SecodaEditorComponentProps): ReactElement => (
		<ReactResourceLink
			href={props.node.attrs.href}
			isSelected={props.isSelected}
		/>
	);

	commands({ type }: NodeOptions) {
		return (attrs?: Record<string, unknown>) =>
			(state: EditorState, dispatch: Dispatch) => {
				let tr = state.tr.insert(
					state.selection.from,
					type.create(attrs, state.schema.text(attrs?.title?.toString() ?? ''))
				);
				tr = tr.insertText(' ');
				dispatch(tr);

				return true;
			};
	}

	toMarkdown(state: MarkdownSerializerState, node: Node) {
		state.write('[');
		if (node.content.size > 0) {
			state.renderContent(node);
		} else {
			state.write(node.attrs.href);
		}
		state.write(']');
		state.write(`(${node.attrs.href})`);
	}

	parseMarkdown() {
		return {
			block: 'resource_link',
			getAttrs: (tok: Token) => ({
				href: tok.attrGet('href'),
			}),
		};
	}
}
